/**
 * Breadcrumb Composable
 * Provides reusable breadcrumb functionality for building navigation trails
 */
import { BREADCRUMB_LABELS, ROLE_SPECIFIC_LABELS } from '@/constants/breadcrumbs';

/**
 * Get display text for a route, applying role-specific overrides
 *
 * @param {string} routeName - The name of the route
 * @param {string} userRole - The current user's role (e.g., 'educator', 'employee')
 * @returns {string} The display text for the breadcrumb
 */
function getDisplayText(routeName, userRole = null) {
    // Get base label from constants
    const baseLabel = BREADCRUMB_LABELS[routeName] || routeName;

    // Check for role-specific overrides
    const roleSpecific = ROLE_SPECIFIC_LABELS[routeName];
    if (roleSpecific && userRole) {
        return roleSpecific[userRole] || roleSpecific.default || baseLabel;
    }

    return baseLabel;
}

/**
 * Find a route by name in the routes array
 *
 * @param {Array} routes - Array of route objects
 * @param {string} routeName - Name of the route to find
 * @returns {Object|null} Route object or null if not found
 */
function findRouteByName(routes, routeName) {
    // Check if any route at this level matches
    const directMatch = routes.find((route) => route.name === routeName);
    if (directMatch) {
        return directMatch;
    }

    // Recursively search in children
    const childMatch = routes
        .filter((route) => route.children)
        .map((route) => findRouteByName(route.children, routeName))
        .find((result) => result !== null);

    return childMatch || null;
}

/**
 * Build the breadcrumb trail by walking up the parent chain
 * Only includes parent routes that exist in the navigation history
 * When parent wasn't visited, checks if previous route should be shown instead
 *
 * @param {Object} currentRoute - The current route object
 * @param {Array} allRoutes - All available routes from the router
 * @param {string} userRole - The current user's role
 * @param {Array} navigationHistory - Array of objects with routeName and fullPath
 * @returns {Array} Array of breadcrumb objects
 */
function buildBreadcrumbChain(currentRoute, allRoutes, userRole, navigationHistory = []) {
    const breadcrumbs = [];
    let route = currentRoute;

    // Add current route to breadcrumbs (use fullPath to preserve IDs)
    const displayText = getDisplayText(route.name, userRole);
    breadcrumbs.unshift({
        link: route.fullPath || route.path,
        text: displayText,
        dim_text: '',
    });

    // Helper to check if a route was visited and get its full path
    const getVisitedRoutePath = (routeName) => {
        const visited = navigationHistory.find((h) => h.routeName === routeName);
        return visited ? visited.fullPath : null;
    };

    // Walk up the parent chain
    while (route) {
        const parentName = route.meta?.breadcrumb?.parent;
        if (!parentName) break;

        const parentRoute = findRouteByName(allRoutes, parentName);
        if (!parentRoute) break;

        // Check if parent was visited
        const parentPath = getVisitedRoutePath(parentName);
        const parentWasVisited = parentPath !== null || parentName === 'dashboard';

        if (parentWasVisited) {
            // Parent was visited - add it to breadcrumbs
            const parentDisplayText = getDisplayText(parentName, userRole);
            breadcrumbs.unshift({
                link: parentPath || parentRoute.path,
                text: parentDisplayText,
                dim_text: '',
            });
            route = parentRoute;
        } else {
            // Parent wasn't visited - check if we should show the previous route instead
            // Look for previous route relative to the CURRENT route in the chain, not originalroute
            const currentRouteName = route.name;
            const routeIndex = navigationHistory.findIndex((h) => h.routeName === currentRouteName);
            const previousRouteData = routeIndex > 0 ? navigationHistory[routeIndex - 1] : null;

            if (previousRouteData) {
                const previousRoute = findRouteByName(allRoutes, previousRouteData.routeName);
                const isDashboard = previousRouteData.routeName === 'dashboard';
                const alreadyInBreadcrumbs = breadcrumbs.some((b) => b.text === getDisplayText(previousRouteData.routeName, userRole));

                // Only include if not already in breadcrumbs, has metadata, and isn't dashboard
                if (previousRoute && previousRoute.meta?.breadcrumb && !isDashboard && !alreadyInBreadcrumbs) {
                    const prevDisplayText = getDisplayText(previousRouteData.routeName, userRole);
                    breadcrumbs.unshift({
                        link: previousRouteData.fullPath,
                        text: prevDisplayText,
                        dim_text: '',
                    });
                }
            }

            // Add dashboard and stop
            const dashboardRoute = findRouteByName(allRoutes, 'dashboard');
            if (dashboardRoute) {
                breadcrumbs.unshift({
                    link: dashboardRoute.path,
                    text: getDisplayText('dashboard', userRole),
                    dim_text: '',
                });
            }
            break;
        }
    }

    // Add dim_text to show context (e.g., "Back to Browse")
    // The dim_text for each breadcrumb is the text of the previous breadcrumb
    breadcrumbs.slice(1).forEach((_, index) => {
        breadcrumbs[index + 1].dim_text = breadcrumbs[index].text;
    });

    return breadcrumbs;
}

/**
 * Build breadcrumb trail from route metadata and navigation history
 * This is the main exported function used by the router guard
 *
 * @param {Object} route - The current route object
 * @param {Array} allRoutes - All available routes from the router
 * @param {string} userRole - The current user's role
 * @param {Array} navigationHistory - Array of visited route names
 * @returns {Array} Array of breadcrumb objects formatted for NebulaBreadcrumbs
 */
export function buildBreadcrumbTrail(route, allRoutes, userRole = null, navigationHistory = []) {
    // If route has no breadcrumb metadata, return empty array
    if (!route.meta?.breadcrumb) {
        return [];
    }

    // Build the breadcrumb chain using navigation history
    const breadcrumbs = buildBreadcrumbChain(route, allRoutes, userRole, navigationHistory);

    // Filter out dashboard if it's the only breadcrumb
    // (BreadcrumbsRow will add it automatically)
    if (breadcrumbs.length === 1 && breadcrumbs[0].text === 'Dashboard') {
        return [];
    }

    return breadcrumbs;
}

/**
 * Composable hook for breadcrumb functionality
 * Use this in components that need breadcrumb utilities
 *
 * @returns {Object} Breadcrumb utility functions
 */
export function useBreadcrumbs() {
    return {
        buildBreadcrumbTrail,
        getDisplayText,
    };
}
